/**
 * Theme core: event bus, overlay manager, media helpers and shared utilities.
 *
 * Loaded once as an ES module and shared through the "@theme/core" entry of
 * the import map in layout/theme.liquid, so every component imports the same
 * instance. No framework: custom elements and native browser APIs only.
 */

/* -------------------------------------------------------------------------
 * Event bus
 *
 * Events are dispatched on `document` with a "theme:" prefix so apps and
 * other scripts can listen without importing this module:
 *   document.addEventListener('theme:cart:updated', (e) => e.detail)
 * ---------------------------------------------------------------------- */

const EVENT_PREFIX = 'theme:';

export const events = {
  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(EVENT_PREFIX + name, { detail }));
  },

  /** Subscribes to an event and returns a function that unsubscribes. */
  on(name, handler, options) {
    const listener = (event) => handler(event.detail, event);
    document.addEventListener(EVENT_PREFIX + name, listener, options);
    return () => document.removeEventListener(EVENT_PREFIX + name, listener, options);
  },
};

/* -------------------------------------------------------------------------
 * Utilities
 * ---------------------------------------------------------------------- */

export const reducedMotion = () =>
  document.documentElement.dataset.motion === 'off' ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const breakpoints = {
  mobile: window.matchMedia('(max-width: 749px)'),
  desktop: window.matchMedia('(min-width: 990px)'),
};

export function debounce(fn, wait = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Resolves once the element finishes its transition, or right away when
 * motion is reduced. A timeout guards against transitions that never fire.
 */
export function afterTransition(element, timeout = 600) {
  if (reducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      element.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (event) => {
      if (event.target === element) done();
    };
    const timer = setTimeout(done, timeout);
    element.addEventListener('transitionend', onEnd);
  });
}

/** Politely announces a message to screen readers. */
export function announce(message) {
  let region = document.getElementById('ThemeAnnouncer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'ThemeAnnouncer';
    region.className = 'visually-hidden';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('role', 'status');
    document.body.append(region);
  }
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

/* -------------------------------------------------------------------------
 * Section rendering
 *
 * Any element with [data-region="name"] can be refreshed from server-rendered
 * section HTML: the matching region in the new markup replaces the inner
 * HTML of every region with that name on the page. Outer elements (such as
 * an open <dialog>) stay in place, so focus and open state survive.
 * ---------------------------------------------------------------------- */

/** Ids of every section on the page that wants cart-driven refreshes. */
export function cartSectionIds() {
  const ids = new Set(['cart-count']);
  document.querySelectorAll('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
  return [...ids];
}

export async function fetchSections(ids, url = window.location.pathname) {
  const separator = url.includes('?') ? '&' : '?';
  const response = await fetch(`${url}${separator}sections=${encodeURIComponent(ids.join(','))}`);
  if (!response.ok) throw new Error(`Section rendering failed: ${response.status}`);
  return response.json();
}

export function applySections(sections) {
  if (!sections) return;
  const parser = new DOMParser();

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('[data-region]').forEach((fresh) => {
      document.querySelectorAll(`[data-region="${CSS.escape(fresh.dataset.region)}"]`).forEach((current) => {
        current.innerHTML = fresh.innerHTML;
      });
    });
  }
}

/* -------------------------------------------------------------------------
 * Overlay manager
 *
 * <overlay-panel name="cart"> wraps a <dialog class="overlay">. Any element
 * with [data-overlay-open="cart"] opens it; [data-overlay-close] inside the
 * panel closes it. Only one overlay is open at a time. Native modal dialogs
 * provide the focus trap, inert background and Escape handling.
 * ---------------------------------------------------------------------- */

export const overlays = {
  current: null,

  get(name) {
    return document.querySelector(`overlay-panel[name="${CSS.escape(name)}"]`);
  },

  open(name, opener) {
    const panel = this.get(name);
    if (!panel) return false;
    panel.show(opener);
    return true;
  },

  close(name) {
    const panel = name ? this.get(name) : this.current;
    return panel?.hide();
  },
};

export class OverlayPanel extends HTMLElement {
  #opener = null;
  #closing = null;

  get dialog() {
    return this.querySelector(':scope > dialog');
  }

  get name() {
    return this.getAttribute('name');
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  connectedCallback() {
    const { dialog } = this;
    if (!dialog) return;

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });

    // Theme editor: panels marked editor-preview open while their section is selected
    if (this.hasAttribute('editor-preview') && window.Shopify?.designMode) {
      this.onSectionSelect = (event) => event.target.contains(this) && this.show(null);
      this.onSectionDeselect = (event) => event.target.contains(this) && this.hide({ restoreFocus: false });
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    this.#setupSheetDrag();

    // A click that lands on the dialog itself (not its surface) hits the backdrop
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.hide();
      if (event.target.closest('[data-overlay-close]')) {
        event.preventDefault();
        this.hide();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    if (overlays.current === this) overlays.current = null;
  }

  async show(opener = document.activeElement) {
    const { dialog } = this;
    if (!dialog) return;

    if (this.#closing) await this.#closing;
    if (dialog.open) return;

    if (overlays.current && overlays.current !== this) {
      await overlays.current.hide({ restoreFocus: false });
    }

    this.#opener = opener instanceof HTMLElement ? opener : null;
    this.#opener?.setAttribute('aria-expanded', 'true');
    overlays.current = this;

    dialog.showModal();
    // Next frame so the closed state is painted before transitioning in
    requestAnimationFrame(() => dialog.setAttribute('data-open', ''));

    events.emit('overlay:open', { name: this.name, panel: this });
  }

  /**
   * Bottom sheets (mobile) can be dragged down by their handle to close.
   * Deliberately simple: follow the pointer, close past a distance or a
   * quick flick, otherwise spring back.
   */
  #setupSheetDrag() {
    const { dialog } = this;
    const handle = dialog.querySelector('[data-sheet-handle]');
    const surface = dialog.querySelector('.overlay__surface');
    if (!handle || !surface || !dialog.classList.contains('overlay--sheet-mobile')) return;

    let startY = 0;
    let startTime = 0;
    let offset = 0;

    const move = (event) => {
      offset = Math.max(0, event.clientY - startY);
      surface.style.setProperty('--sheet-offset', `${offset}px`);
    };

    const end = () => {
      handle.removeEventListener('pointermove', move);
      dialog.removeAttribute('data-dragging');
      const velocity = offset / Math.max(1, Date.now() - startTime);
      const shouldClose = offset > surface.offsetHeight * 0.25 || velocity > 0.6;
      surface.style.removeProperty('--sheet-offset');
      if (shouldClose) this.hide();
    };

    handle.addEventListener('pointerdown', (event) => {
      if (!breakpoints.mobile.matches) return;
      startY = event.clientY;
      startTime = Date.now();
      offset = 0;
      handle.setPointerCapture(event.pointerId);
      dialog.setAttribute('data-dragging', '');
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', end, { once: true });
      handle.addEventListener('pointercancel', end, { once: true });
    });
  }

  hide({ restoreFocus = true } = {}) {
    const { dialog } = this;
    if (!dialog?.open) return Promise.resolve();
    if (this.#closing) return this.#closing;

    const surface = dialog.querySelector('.overlay__surface') || dialog;
    dialog.removeAttribute('data-open');

    this.#closing = afterTransition(surface).then(() => {
      dialog.close();
      this.#closing = null;
      if (overlays.current === this) overlays.current = null;

      this.#opener?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
      this.#opener = null;

      events.emit('overlay:close', { name: this.name, panel: this });
    });

    return this.#closing;
  }
}

if (!customElements.get('overlay-panel')) customElements.define('overlay-panel', OverlayPanel);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-overlay-open]');
  if (!trigger) return;
  if (overlays.open(trigger.dataset.overlayOpen, trigger)) event.preventDefault();
});

/* -------------------------------------------------------------------------
 * Media helpers
 *
 * <autoplay-video> plays its muted, looping <video> only while it is visible
 * and motion is allowed, and exposes a pause/play toggle (WCAG 2.2.2).
 * Add data-autoplay="false" to start paused until the visitor presses play.
 * A hidden (display: none) video never intersects, so desktop/mobile
 * variants only load and play where they are shown.
 * ---------------------------------------------------------------------- */

class AutoplayVideo extends HTMLElement {
  #observer;
  #userPaused = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');
    if (!this.video) return;

    this.#userPaused = this.dataset.autoplay === 'false';
    this.video.addEventListener('playing', () => this.setAttribute('data-playing', ''));

    this.toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#userPaused = !this.video.paused;
      if (this.#userPaused) {
        this.video.pause();
      } else {
        this.video.preload = 'auto';
        this.video.play().catch(() => {});
      }
      this.#sync();
    });

    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.#userPaused && !reducedMotion()) {
        this.video.preload = 'auto';
        this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }
      this.#sync();
    });
    this.#observer.observe(this);
    this.video.addEventListener('pause', () => this.#sync());
    this.video.addEventListener('play', () => this.#sync());
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #sync() {
    const paused = this.video.paused;
    this.toggle?.setAttribute('aria-pressed', String(paused));
    const label = this.toggle?.querySelector('[data-video-label]');
    if (label) label.textContent = paused ? label.dataset.play : label.dataset.pause;
  }
}

if (!customElements.get('autoplay-video')) customElements.define('autoplay-video', AutoplayVideo);

/* -------------------------------------------------------------------------
 * Reveal on scroll
 *
 * Elements with [data-reveal] get [data-revealed] the first time they enter
 * the viewport; CSS in critical.css handles the (subtle) entrance. Nothing
 * is hidden when motion is reduced or inside the theme editor.
 * ---------------------------------------------------------------------- */

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.setAttribute('data-revealed', '');
      revealObserver.unobserve(entry.target);
    });
  },
  { rootMargin: '0px 0px -10% 0px' },
);

export function observeReveals(root = document) {
  root.querySelectorAll('[data-reveal]:not([data-revealed])').forEach((el) => revealObserver.observe(el));
}

observeReveals();
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));

/* -------------------------------------------------------------------------
 * Storage: localStorage that never throws (private mode, blocked storage)
 * ---------------------------------------------------------------------- */

export const storage = {
  get(key, fallback = null) {
    try {
      const value = window.localStorage.getItem(key);
      return value === null ? fallback : JSON.parse(value);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  },
};
